import React, { useEffect, useState, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { Navbar } from './components/Navbar';
import { Footer } from './components/Footer';
import { LiveChat } from './components/LiveChat';
import { StickyMobileCTA } from './components/StickyMobileCTA';
import { ScrollToTopButton } from './components/ScrollToTopButton';
import { PageTransition } from './components/PageTransition';
// Home is the landing route — keep it eager so the first paint is instant.
import { Home } from './pages/Home';
import { CartProvider } from './components/CartContext';
import { CartDrawer } from './components/CartDrawer';
import { AnnouncementBar } from './components/AnnouncementBar';
import { I18nProvider } from './utils/i18n';
// Every other route is code-split and fetched on demand, so they no longer
// weigh down the initial bundle. Named exports are mapped to a default for lazy.
const Contact = lazy(() => import('./pages/Contact').then(m => ({
  default: m.Contact
})));
const Product = lazy(() => import('./pages/Product').then(m => ({
  default: m.Product
})));
const Project = lazy(() => import('./pages/Project').then(m => ({
  default: m.Project
})));
const HelpMeChoose = lazy(() => import('./pages/HelpMeChoose').then(m => ({
  default: m.HelpMeChoose
})));
const AboutPage = lazy(() => import('./pages/AboutPage').then(m => ({
  default: m.AboutPage
})));
const DeliveryZones = lazy(() => import('./pages/DeliveryZones').then(m => ({
  default: m.DeliveryZones
})));
const Subcontractors = lazy(() => import('./pages/Subcontractors').then(m => ({
  default: m.Subcontractors
})));
const Blog = lazy(() => import('./pages/Blog').then(m => ({
  default: m.Blog
})));
const BlogPost = lazy(() => import('./pages/BlogPost').then(m => ({
  default: m.BlogPost
})));
const DeliveryPrep = lazy(() => import('./pages/DeliveryPrep').then(m => ({
  default: m.DeliveryPrep
})));
const CompareMaterials = lazy(() => import('./pages/CompareMaterials').then(m => ({
  default: m.CompareMaterials
})));
const NotFound = lazy(() => import('./pages/NotFound').then(m => ({
  default: m.NotFound
})));
const TrackOrder = lazy(() => import('./pages/TrackOrder').then(m => ({
  default: m.TrackOrder
})));
const TrackOrderLookup = lazy(() => import('./pages/TrackOrderLookup').then(m => ({
  default: m.TrackOrderLookup
})));
const Calculator = lazy(() => import('./pages/Calculator').then(m => ({
  default: m.Calculator
})));
const LocalVsNational = lazy(() => import('./pages/LocalVsNational').then(m => ({
  default: m.LocalVsNational
})));
const CityMaterial = lazy(() => import('./pages/CityMaterial').then(m => ({
  default: m.CityMaterial
})));
const ServiceAreas = lazy(() => import('./pages/ServiceAreas').then(m => ({
  default: m.ServiceAreas
})));
const LogoConcepts = lazy(() => import('./components/LogoConcepts').then(m => ({
  default: m.LogoConcepts
})));
const RefundPolicy = lazy(() => import('./pages/RefundPolicy').then(m => ({
  default: m.RefundPolicy
})));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy').then(m => ({
  default: m.PrivacyPolicy
})));
const TermsOfService = lazy(() => import('./pages/TermsOfService').then(m => ({
  default: m.TermsOfService
})));
const DailyTicketForm = lazy(() => import('./pages/DailyTicketForm').then(m => ({
  default: m.DailyTicketForm
})));
function RouteFallback() {
  return <div className="min-h-[60vh] flex items-center justify-center bg-[#fdfbf7] dark:bg-[#222222]" role="status" aria-label="Loading" data-id="element-0" data-mp="1w33qmq9chddh">
      <span className="h-8 w-8 rounded-full border-2 border-brand-orange border-t-transparent animate-spin" data-id="element-1" data-mp="1i2j9dyj2vpm3" />
    </div>;
}
function ScrollToHash() {
  const location = useLocation();
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      // Two-pass scroll: first attempt accounts for the sticky navbar height,
      // then a follow-up corrects for late-rendering content (Services, etc.)
      // that can push the target further down the page.
      const scrollToTarget = () => {
        const el = document.getElementById(id);
        if (!el) return;
        const navHeight = document.querySelector('nav')?.getBoundingClientRect().height || 80;
        const top = el.getBoundingClientRect().top + window.scrollY - navHeight - 12;
        window.scrollTo({
          top: Math.max(0, top),
          behavior: 'smooth'
        });
      };
      const t1 = setTimeout(scrollToTarget, 120);
      const t2 = setTimeout(scrollToTarget, 600);
      return () => {
        clearTimeout(t1);
        clearTimeout(t2);
      };
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);
  return null;
}
function AnimatedRoutes() {
  const location = useLocation();
  return <AnimatePresence mode="wait" data-id="element-2" data-mp="3kpduwmns46l7">
      <Suspense fallback={<RouteFallback data-id="element-4" data-mp="3glxxvp7ss47z" />} data-id="element-3" data-mp="3gmhaqrilc2f5">
        <Routes location={location} key={location.pathname} data-id="element-5" data-mp="3glob0ai1ud6i">
          <Route path="/" element={<PageTransition data-id="element-7" data-mp="1w5gd6omrhs37">
                <Home data-id="element-8" data-mp="1w69lvv5a6rnz" />
              </PageTransition>} data-id="element-6" data-mp="1w4ylm5b1o21m" />
          <Route path="/contact" element={<PageTransition data-id="element-10" data-mp="206jrv7t2pi2u">
                <Contact data-id="element-11" data-mp="203nylws67hkq" />
              </PageTransition>} data-id="element-9" data-mp="2058rlhyu6sgh" />
          <Route path="/shop/:slug" element={<PageTransition data-id="element-13" data-mp="253a8bw8sifos">
                <Product data-id="element-14" data-mp="29wd6khoiidbb" />
              </PageTransition>} data-id="element-12" data-mp="254bmwap0lkb9" />
          <Route path="/projects/:slug" element={<PageTransition data-id="element-16" data-mp="2evfzj3r7wt2x">
                <Project data-id="element-17" data-mp="2eu4xb8df7tdx" />
              </PageTransition>} data-id="element-15" data-mp="29xwla3uisgaz" />
          <Route path="/help" element={<PageTransition data-id="element-19" data-mp="2jsyaezospidk">
                <HelpMeChoose data-id="element-20" data-mp="2js6flr30vooc" />
              </PageTransition>} data-id="element-18" data-mp="2jtr87b5s0xjk" />
          <Route path="/about" element={<PageTransition data-id="element-22" data-mp="2nw4djygm2tft">
                <AboutPage data-id="element-23" data-mp="2supfc2bihl6d" />
              </PageTransition>} data-id="element-21" data-mp="2nvcu1m0agm5w" />
          <Route path="/delivery-zones" element={<PageTransition data-id="element-25" data-mp="2ssbjhr90c4xr">
                <DeliveryZones data-id="element-26" data-mp="tid8lc988xqp" />
              </PageTransition>} data-id="element-24" data-mp="2su5w8ia4kqqr" />
          <Route path="/subcontractors" element={<PageTransition data-id="element-28" data-mp="yfv8ye96i4n3">
                <Subcontractors data-id="element-29" data-mp="yh6jqy11kcnp" />
              </PageTransition>} data-id="element-27" data-mp="tjy3j2zgeip3" />
          <Route path="/contractor-ordering" element={<PageTransition data-id="element-31" data-mp="21ru7dg9a8jgx">
                <Subcontractors data-id="element-32" data-mp="1wsr15shkg77d" />
              </PageTransition>} data-id="element-30" data-mp="21qskmeiwm8jd" />
          <Route path="/diy-help" element={<Navigate to="/blog/perfect-gravel-driveway" replace data-id="element-34" data-mp="2as8rwdef9ujd" />} data-id="element-33" data-mp="1wubu5do8fi34" />
          <Route path="/blog" element={<PageTransition data-id="element-36" data-mp="25t5pl0ptu2f3">
                <Blog data-id="element-37" data-mp="25s4ckqp935uq" />
              </PageTransition>} data-id="element-35" data-mp="2atjvac3yh09y" />
          <Route path="/blog/:slug" element={<PageTransition data-id="element-39" data-mp="2kpmyzmegh6z0">
                <BlogPost data-id="element-40" data-mp="2kovdj4ekopmg" />
              </PageTransition>} data-id="element-38" data-mp="25vie5wq3bb14" />
          <Route path="/delivery-prep" element={<PageTransition data-id="element-42" data-mp="2fqtwep0pj0dm">
                <DeliveryPrep data-id="element-43" data-mp="2fpirumzftolg" />
              </PageTransition>} data-id="element-41" data-mp="2frv7gthq3mvc" />
          <Route path="/compare" element={<PageTransition data-id="element-45" data-mp="2tkupp38w3v6l">
                <CompareMaterials data-id="element-46" data-mp="2pgmviqjnxn0z" />
              </PageTransition>} data-id="element-44" data-mp="2thgldpgo1bwi" />
          <Route path="/calculator" element={<PageTransition data-id="element-48" data-mp="33gefd7wv1xmw">
                <Calculator data-id="element-49" data-mp="33hpkpc5yfdg4" />
              </PageTransition>} data-id="element-47" data-mp="2pj9b9dgpj8ua" />
          <Route path="/local-vs-national" element={<PageTransition data-id="element-51" data-mp="2yj5z5cv9w3oa">
                <LocalVsNational data-id="element-52" data-mp="2yg05rlfm2fmr" />
              </PageTransition>} data-id="element-50" data-mp="2yi3731m7rv6s" />
          <Route path="/track" element={<PageTransition data-id="element-54" data-mp="3i1k05nl3u7tm">
                <TrackOrderLookup data-id="element-55" data-mp="3n39a0zwlcfg4" />
              </PageTransition>} data-id="element-53" data-mp="3i4xxghewvqu7" />
          <Route path="/track/:orderId" element={<PageTransition data-id="element-57" data-mp="385yqd39so39b">
                <TrackOrder data-id="element-58" data-mp="384xdct97x6oy" />
              </PageTransition>} data-id="element-56" data-mp="3n0n0iuq53pv9" />
          <Route path="/service-areas" element={<PageTransition data-id="element-60" data-mp="3d38qpdyq1zbx">
                <ServiceAreas data-id="element-61" data-mp="3d6cyelbebd7c" />
              </PageTransition>} data-id="element-59" data-mp="3d40jkh0xpiyi" />
          <Route path="/logo-concepts" element={<PageTransition data-id="element-63" data-mp="2zbal3d47ylki">
                <LogoConcepts data-id="element-64" data-mp="344djbyjxyj71" />
              </PageTransition>} data-id="element-62" data-mp="2zcdbljxmqlzw" />
          <Route path="/refund-policy" element={<PageTransition data-id="element-66" data-mp="2pfh9px28ky4b">
                <RefundPolicy data-id="element-67" data-mp="2pgini9amzypq" />
              </PageTransition>} data-id="element-65" data-mp="345y8f8nhl9xi" />
          <Route path="/privacy-policy" element={<PageTransition data-id="element-69" data-mp="2uek50psezpz3">
                <PrivacyPolicy data-id="element-70" data-mp="2ubpk6x5ho355" />
              </PageTransition>} data-id="element-68" data-mp="2udisshznwxfs" />
          <Route path="/terms-of-service" element={<PageTransition data-id="element-72" data-mp="2kfuyft5yxry5">
                <TermsOfService data-id="element-73" data-mp="2kg4d4klkccoj" />
              </PageTransition>} data-id="element-71" data-mp="2gfkjvs441t7o" />
          <Route path="/form" element={<PageTransition data-id="element-75" data-mp="30vx1zq935nim">
                <DailyTicketForm data-id="element-76" data-mp="30uveunespakj" />
              </PageTransition>} data-id="element-74" data-mp="2khpbklb00fro" />

          <Route path="/gravel-delivery/:citySlug/:materialSlug" element={<PageTransition data-id="element-78" data-mp="2vug320a5wb4o">
                <CityMaterial data-id="element-79" data-mp="2qwob569iop0h" />
              </PageTransition>} data-id="element-77" data-mp="2vvr9k7uzrwzh" />
          <Route path="*" element={<PageTransition data-id="element-81" data-mp="2lyd22xr1qijb">
                <NotFound data-id="element-82" data-mp="2lx32u58gj4b3" />
              </PageTransition>} data-id="element-80" data-mp="2qzaib4shx2i6" />
        </Routes>
      </Suspense>
    </AnimatePresence>;
}
function AppContent({
  isDark,
  toggleDark
}: {
  isDark: boolean;
  toggleDark: () => void;
}) {
  const location = useLocation();
  const isContractorPage = location.pathname.includes('subcontractors') || location.pathname.includes('contractor');
  // The daily ticket form (/form) is a standalone operational document. It
  // renders bare — no site chrome (navbar, footer, announcement bar, live
  // chat, sticky CTA, cart) — so it prints clean and stays out of the way.
  const isStandalonePage = location.pathname === '/form';
  if (isStandalonePage) {
    // The daily ticket is a printed white document — never apply the `dark`
    // class here so it always renders on a clean white surface regardless of
    // the visitor's theme.
    return <div className="min-h-screen font-sans" data-id="element-83" data-mp="ipuac7j319u1">
        <ScrollToHash data-id="element-84" data-mp="iq41hyfv5n1b" />
        <AnimatedRoutes data-id="element-85" data-mp="inhrzt9ewxgg" />
      </div>;
  }
  return <div className={`min-h-screen flex flex-col font-sans ${isDark ? 'dark' : ''} ${isContractorPage ? 'contractor-theme' : ''}`} data-id="element-86" data-mp="noxfdrz2bg20">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[9999] focus:px-4 focus:py-2 focus:bg-brand-orange focus:text-white focus:rounded-lg focus:font-bold focus:outline-none focus:ring-2 focus:ring-white" data-id="element-87" data-mp="3m7w1o045ldal">
        Skip to main content
      </a>
      <div className="flex-grow bg-[#fdfbf7] dark:bg-[#222222] transition-colors duration-300 pb-16 md:pb-0" id="main-content" data-id="element-88" data-mp="4r4bongm723k">
        <ScrollToHash data-id="element-89" data-mp="38ewm5y4dfj31" />
        <AnnouncementBar data-id="element-90" data-mp="38eczk43s06io" />
        <Navbar isDark={isDark} toggleDark={toggleDark} data-id="element-91" data-mp="38dlgttva239t" />
        <AnimatedRoutes data-id="element-92" data-mp="2f7dkkmju90mp" />
        <Footer data-id="element-93" data-mp="2f63kjrtfdidf" />
        <LiveChat data-id="element-94" data-mp="2f6lpdcu5l4wy" />
        <StickyMobileCTA data-id="element-95" data-mp="2f97wjbd4tien" />
        <ScrollToTopButton data-id="element-96" data-mp="2k685bu1d8z9g" />
        <CartDrawer data-id="element-97" data-mp="2k5f1p1xpepvj" />
      </div>
    </div>;
}
// Decide the initial theme for a first-time visitor (no saved preference yet):
//   1. An explicit saved choice always wins.
//   2. Otherwise honor the device's OS setting (prefers-color-scheme).
//   3. If the device expresses no preference, fall back to the local clock:
//      light during daylight hours (roughly 6am–7pm), dark after sunset.
function getInitialTheme(): boolean {
  try {
    const saved = localStorage.getItem('theme');
    if (saved) {
      return saved === 'dark';
    }
  } catch {
    // localStorage can throw in private mode / embedded webviews / when
    // storage is blocked. Fall through to the device/time defaults.
  }
  if (typeof window !== 'undefined' && window.matchMedia) {
    if (window.matchMedia('(prefers-color-scheme: dark)').matches) return true;
    if (window.matchMedia('(prefers-color-scheme: light)').matches) return false;
  }
  // Time-of-day fallback: dark after sunset / before sunrise.
  const hour = new Date().getHours();
  return hour < 6 || hour >= 19;
}
export function App() {
  const [isDark, setIsDark] = useState(getInitialTheme);
  useEffect(() => {
    try {
      localStorage.setItem('theme', isDark ? 'dark' : 'light');
    } catch {}
    if (isDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDark]);
  const toggleDark = () => setIsDark(!isDark);
  return <I18nProvider data-id="element-98" data-mp="348cfjc8jhluw">
      <Router data-id="element-99" data-mp="347jemrw9hqko">
        <CartProvider data-id="element-100" data-mp="349dzy7bg3kpa">
          <AppContent isDark={isDark} toggleDark={toggleDark} data-id="element-101" data-mp="348kr90sxel4i" />
        </CartProvider>
      </Router>
    </I18nProvider>;
}